import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  DataSourceSearchResult,
  FacilitySearchResult,
  FeatureResult,
  SearchParams,
  SearchResult,
  ShipmentSearchResult,
} from '@src/app/shared/models';
import { httpOptions } from '@src/app/shared/utils/http.utils';
import { environment } from '@src/environments/environment';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private http = inject(HttpClient);


  search(params: SearchParams): Observable<SearchResult> {
    const fetch$ = {
      shipments: this.searchShipments(params),
      facilities: this.searchFacilities(params),
      dataSources: this.searchDataSources(params),
    };

    return forkJoin(fetch$).pipe(
      map((res) => {
        return {
          shipments: res.shipments,
          facilities: res.facilities,
          dataSources: res.dataSources,
        };
      }),
      catchError((err) => {
        console.error(err);
        return of({
          shipments: {
            content: [],
            last: true,
            number: 0,
            size: 100,
            totalElements: 0,
          },
          dataSources: {
            content: [],
            last: true,
            number: 0,
            size: 100,
            totalElements: 0,
          },
          facilities: {
            content: [],
            last: true,
            number: 0,
            size: 100,
            totalElements: 0,
          }
        });
      })
    );
  }

  searchShipments({
    q,
    page = 0,
  }: SearchParams): Observable<FeatureResult<ShipmentSearchResult>> {
    const url = `${environment.appUrlPrefix}/v3/universal-search/shipments`;
    return this.http.get<FeatureResult<ShipmentSearchResult>>(
      url, { params: { searchTerm: q, page, size: 10 }, ...httpOptions }
    ).pipe(
      // TODO: When we update to Angular version 18, remove this and use @let to define logic in the template.
      map(res => {
        return {
          content: res.content.map(content => {
            const devices = content.item.devices.filter((device) => {
              return device.deviceNumber.toLowerCase().includes(q.toLowerCase());
            });
            const references = content.item.references.filter((reference) => {
              return reference.value.toLowerCase().includes(q.toLowerCase());
            });

            return {
              highlight: content.highlight,
              item: {
                ...content.item,
                devices,
                references
              },
            };
          }),
          totalElements: res.totalElements,
        }
      }),
      catchError((err) => {
        console.error(err);
        return of({
          content: [],
          totalElements: 0
        });
      })
    );
  }

  searchDataSources({
    q,
    page = 0,
  }: SearchParams): Observable<FeatureResult<DataSourceSearchResult>> {
    const url = `${environment.appUrlPrefix}/v3/universal-search/dataSources`;
    return this.http.get<FeatureResult<DataSourceSearchResult>>(
      url, { params: { searchTerm: q, page, size: 10 }, ...httpOptions }
    ).pipe(
      catchError(() => {
        return of({
          content: [],
          totalElements: 0,
        });
      })
    );
  }

  searchFacilities({
    q,
    page = 0,
  }: SearchParams): Observable<FeatureResult<FacilitySearchResult>> {
    const url = `${environment.appUrlPrefix}/v3/universal-search/facilities`;
    return this.http.get<FeatureResult<FacilitySearchResult>>(
      url, { params: { searchTerm: q, page, size: 10 }, ...httpOptions }
    ).pipe(
      // TODO: When we update to Angular version 18, remove this and use @let to define logic in the template.
      map(res => {
        return {
          content: res.content.map(content => {
            const labels = content.item.labels.filter((label) => {
              return label.toLowerCase().includes(q.toLowerCase());
            });
            const address = content.item.address.toLowerCase().includes(q.toLowerCase()) ? content.item.address : '';

            return {
              highlight: content.highlight,
              item: {
                ...content.item,
                address,
                labels
              },
            };
          }),
          totalElements: res.totalElements,
        }
      }),
      catchError((err) => {
        console.error(err);
        return of({
          content: [],
          totalElements: 0
        });
      })
    );
  }
}
